import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';

export interface GroupedStrategy {
  strategy: CampaignDesiredStateDto['productStrategies'][0];
  index: number;
}

export interface AbTestingGroup {
  groupId: string;
  strategies: GroupedStrategy[];
  representativeIndex: number; // Index of the first strategy in the group
}

export interface GroupedStrategiesResult {
  abTestingGroups: AbTestingGroup[];
  ungroupedStrategies: GroupedStrategy[];
}

/**
 * Groups product strategies by their abTestingGroupId
 * Only groups with multiple strategies are considered true A/B testing groups
 * @param strategies - Array of product strategies from the campaign
 * @returns Object containing grouped A/B testing strategies and ungrouped strategies
 */
export function groupStrategiesByAbTesting (
  strategies: CampaignDesiredStateDto['productStrategies']
): GroupedStrategiesResult {
  const groupMap = new Map<string, GroupedStrategy[]>();
  const ungroupedStrategies: GroupedStrategy[] = [];

  strategies.forEach((strategy, index) => {
    const groupId = strategy.groupId;

    if (groupId) {
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, []);
      }
      groupMap.get(groupId)!.push({ strategy, index });
    } else {
      ungroupedStrategies.push({ strategy, index });
    }
  });

  // Only groups with multiple strategies are true A/B testing groups
  const abTestingGroups: AbTestingGroup[] = [];

  groupMap.forEach((strategies, groupId) => {
    if (strategies.length > 1) {
      // Multiple strategies with same groupId = true A/B testing group
      abTestingGroups.push({
        groupId,
        strategies,
        representativeIndex: strategies[0].index,
      });
    } else {
      // Single strategy with groupId = treat as ungrouped
      ungroupedStrategies.push(...strategies);
    }
  });

  return {
    abTestingGroups,
    ungroupedStrategies,
  };
}

/**
 * Gets product set information (ID and name) for an A/B testing group
 * @param strategies - Array of strategies in the group
 * @returns Array of objects with productSetId and productSetName
 */
export function getProductSetsForGroup (strategies: GroupedStrategy[]): Array<{ productSetId: string; productSetName?: string }> {
  return strategies
    .filter(({ strategy }) => strategy.productSetId !== undefined && strategy.productSetId !== null && strategy.productSetId !== '')
    .map(({ strategy }) => ({
      productSetId: strategy.productSetId!,
      productSetName: strategy.productSetName,
    }));
}

export interface UniqueTemplateInfo {
  templateId: string;
  templateConfig: CampaignDesiredStateDto['productStrategies'][0]['benderTemplates'][0];
  strategyInfo: {
    productSetId?: string;
    identifier?: string;
  };
}

/**
 * Extracts unique template configurations from product strategies
 * For A/B testing, multiple strategies share the same templates, so we deduplicate by templateId
 * @param strategies - Array of product strategies from the campaign
 * @returns Array of unique template configurations with associated strategy info
 */
export function getUniqueTemplatesFromStrategies (
  strategies: CampaignDesiredStateDto['productStrategies']
): UniqueTemplateInfo[] {
  const uniqueTemplates = new Map<string, UniqueTemplateInfo>();

  strategies.forEach(strategy => {
    strategy.benderTemplates.forEach(templateConfig => {
      const templateId = templateConfig.templateId;
      if (!uniqueTemplates.has(templateId)) {
        uniqueTemplates.set(templateId, {
          templateId,
          templateConfig,
          strategyInfo: {
            productSetId: strategy.productSetId,
            identifier: strategy.pageId ?? strategy.strategyId,
          },
        });
      }
    });
  });

  return Array.from(uniqueTemplates.values());
}
